<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import type { ChapterConfig } from '../configs/types'
import { getChapterConfig } from '../configs/brands'
import { usePurchase } from '../composables/usePurchase'
import { useSubscription } from '../composables/useSubscription'
import { createShowPaymentMethodSelector } from '../composables/usePaymentMethodDisplay'
import SubscribeItemContent from './SubscribeItemContent.vue'
import PurchaseItemContent from './PurchaseItemContent.vue'
import PaymentMethodSelector from './PaymentMethodSelector.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useUserStore } from '@/stores'
import 'swiper/css'
import 'swiper/css/pagination'
import { colors } from '@/config/brands'

const props = defineProps<{
  value: boolean
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'success'): void
}>()

// 加载当前品牌的 chapter 配置
const config: ChapterConfig = getChapterConfig()

const userStore = useUserStore()

// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplateSuccess,
  PayTemplate,
  currentPaymentMethod: purchasePaymentMethod,
  availablePaymentMethods: purchaseAvailableMethods,
  selectPaymentMethod: selectPurchasePaymentMethod,
  productLists: purchaseProductLists,
} = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})

// 使用订阅 composable（支持多支付方式）
const {
  loaded,
  subscribeSuccess,
  getUnitPrice,
  onSubscribeProducts,
  getIsImageBackground,
  isZero,
  availablePaymentMethods: subscriptAvailableMethods,
  selectPaymentMethod: selectSubscriptPaymentMethod,
  currentPaymentMethod: subscriptPaymentMethod,
  hasPayPalPasswordlessPayment,
} = useSubscription('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})

const active = ref(0)
const swiperInstance = ref<Swiper | null>(null)
const swiperInitTimer = ref<NodeJS.Timeout | null>(null)

// 统一的支付方式状态
const currentPaymentMethod = computed({
  get: () => {
    if (!purchaseProductLists.value || purchaseProductLists.value.length === 0)
      return subscriptPaymentMethod.value
    return purchasePaymentMethod.value
  },
  set: (val) => {
    selectPurchasePaymentMethod(val)
    selectSubscriptPaymentMethod(val)
  },
})

// 统一的可用支付方式
const availablePaymentMethods = computed(() => {
  if (hasPayPalPasswordlessPayment.value) {
    const all = [...(purchaseAvailableMethods.value || []), ...(subscriptAvailableMethods.value || [])]
    const mergedUnique = all.filter((m, i, arr) =>
      arr.findIndex(x => String(x.paymentMethod).toLowerCase() === String(m.paymentMethod).toLowerCase()) === i,
    )
    return mergedUnique.filter(m => String(m.paymentMethod).toLowerCase() === 'paypal')
  }

  const purchaseMethods = purchaseAvailableMethods.value || []
  const subscriptMethods = subscriptAvailableMethods.value || []

  if (purchaseMethods.length > subscriptMethods.length)
    return purchaseMethods
  if (subscriptMethods.length > purchaseMethods.length)
    return subscriptMethods

  if (purchaseMethods.length === 1 && subscriptMethods.length === 1) {
    const p = String(purchaseMethods[0]?.paymentMethod ?? '')
    const s = String(subscriptMethods[0]?.paymentMethod ?? '')
    if (p === s)
      return purchaseMethods
    const merged = [...purchaseMethods, ...subscriptMethods].filter(
      (m, i, arr) => arr.findIndex(x => String(x.paymentMethod) === String(m.paymentMethod)) === i,
    )
    return merged
  }

  return purchaseMethods
})

const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

const subscribes = computed(() => {
  const currentMethod = Number(currentPaymentMethod.value)
  if (!subscriptAvailableMethods.value || !Array.isArray(subscriptAvailableMethods.value))
    return []
  const currentMethodList = subscriptAvailableMethods.value.find(
    (list: any) => Number(list?.paymentMethod) === currentMethod,
  )
  if (!currentMethodList?.products || !Array.isArray(currentMethodList.products))
    return []
  return currentMethodList.products.filter((item: any) => item && item.purchasePopShow)
})

const hasOnlySubscribes = computed(() => {
  return (!templateProducts.value || templateProducts.value.length === 0) && subscribes.value?.length > 0
})

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function handleSubscribeProducts(item: any) {
  // 确保订阅使用统一的支付方式（避免两个 hook 各自初始化后状态不一致）
  selectSubscriptPaymentMethod(currentPaymentMethod.value)
  onSubscribeProducts(item)
}

function initSwiper() {
  if (swiperInitTimer.value) {
    clearTimeout(swiperInitTimer.value)
    swiperInitTimer.value = null
  }

  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }

  if (!subscribes.value?.length || hasOnlySubscribes.value)
    return

  nextTick(() => {
    if (!subscribes.value?.length || hasOnlySubscribes.value)
      return

    swiperInstance.value = new Swiper('.banner.swiper', {
      modules: [Pagination, Autoplay],
      spaceBetween: 0,
      autoplay: {
        delay: 3500,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      loop: subscribes.value.length > 1,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      resizeObserver: true,
    })
  })
}

function shouldInitSwiper() {
  return loaded.value && props.value && subscribes.value?.length > 0 && !hasOnlySubscribes.value
}

watch(
  () => props.value,
  (newVal) => {
    if (newVal && shouldInitSwiper())
      initSwiper()
  },
)

watch(
  () => loaded.value,
  (val) => {
    if (val && shouldInitSwiper())
      initSwiper()
  },
)

watch(
  () => subscribes.value,
  (newVal) => {
    if (loaded.value && newVal?.length && props.value && !hasOnlySubscribes.value)
      initSwiper()
  },
)

watch(
  currentPaymentMethod,
  () => {
    if (shouldInitSwiper()) {
      initSwiper()
    }
    else {
      if (swiperInstance.value) {
        swiperInstance.value.destroy(true, true)
        swiperInstance.value = null
      }
    }
  },
)

function handleVisibilityChange() {
  if (!document.hidden && shouldInitSwiper())
    initSwiper()
}

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true)
    swiperInstance.value = null
  }
})

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

// Axon view_item 上报：充值和订阅各自独立实例，Set 内部去重
const { reportViewItem: reportPurchaseViewItem } = useAxonViewItem()
const { reportViewItem: reportSubscribeViewItem } = useAxonViewItem()

watch(templateProducts, (val) => {
  if (val.length)
    reportPurchaseViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

watch(subscribes, (val) => {
  if (val.length)
    reportSubscribeViewItem(val, currentPaymentMethod.value, 'Membership', p => p.preferentialPrice ?? p.price)
})

const vipBg = config.colors.vipBg
const purpleTextGradient = config.colors.purpleTextGradient
const pinkGradient = config.colors.pinkGradient

function getSubscribeItemStyle(item: any) {
  return {
    background: getIsImageBackground(item)
      ? `url(${config.colors.memberSubscribeBg}) no-repeat`
      : config.colors.subscribeActiveBg,
    backgroundSize: '100% 100%',
  }
}
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)">
    <div class="popup-recharge w-full px-5 pt-20px pb-30px bg-n-bg-card rounded-t-lg relative">
      <h3 class="text-18px text-center text-n-text-1">
        {{ config.texts.payTemplateTitle }}
      </h3>
      <p class="text-14px text-n-text-2 text-center">
        {{ config.texts.payTemplateDesc }}
      </p>
      <template v-if="showPaymentMethodSelector">
        <PaymentMethodSelector
          :dark="config.darkPaymentSelector"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="val => currentPaymentMethod = val"
        />
      </template>

      <!-- 充值项（网格布局） -->
      <div class="flex flex-wrap justify-between">
        <div
          v-for="(item, index) in templateProducts"
          :key="index"
          class="purchase-item-wrapper relative b-rd-8px w-48% mt-4"
          :style="{
            border: active === index ? `1px solid ${colors?.primary}` : `1px solid ${colors?.border.primary}`,
            background: active === index ? config.colors.vipBg : colors?.bg.card,
          }"
          @click="handlePayTemplate(item, index)"
        >
          <PurchaseItemContent :item="item" :is-active="active === index" />
        </div>
      </div>

      <!-- 订阅 - 列表样式（只有订阅商品时） -->
      <div v-if="!userStore.$state.subscribe && subscribes?.length && hasOnlySubscribes" class="subscribe-list-container mt-4">
        <div class="subscribe-list">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="subscribe-list-item rounded-8px mb-3"
            :style="getSubscribeItemStyle(item)"
            @click="handleSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
      </div>

      <!-- 订阅 - 轮播样式（有充值商品时） -->
      <div v-else-if="!userStore.$state.subscribe && subscribes?.length" class="banner swiper mt-4">
        <div class="swiper-wrapper">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="swiper-slide swiper-item rounded-8px"
            :style="getSubscribeItemStyle(item)"
            @click="handleSubscribeProducts(item)"
          >
            <SubscribeItemContent :item="item" :get-unit-price="getUnitPrice" :is-zero="isZero" />
          </div>
        </div>
        <div class="swiper-pagination flex items-center justify-center" />
      </div>

      <div v-if="!loaded" class="h-260px flex items-center justify-center flex-col">
        <BasicSpinner class="mx-auto" />
        <div class="mt-2 font-bold">
          {{ config.texts.loading }}
        </div>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.pay-template-info:hover {
  border: 1px solid var(--primary-color);
  background-color: v-bind(vipBg);
}

.swiper-item {
  height: 115px;
}

@media screen and (min-width: 380px) {
  .swiper-item {
    min-height: 91px;
  }
}

.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.subscribe-list-container {
  max-height: 360px;
  overflow: hidden;
  position: relative;
}

.subscribe-list {
  max-height: 360px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
  overscroll-behavior: contain;
  touch-action: pan-y;
}

.subscribe-list-item {
  min-height: 91px;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }

  &:last-child {
    margin-bottom: 0 !important;
  }
}
</style>

<style lang="scss">
.popup-recharge {
  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 0.5;
  }

  .swiper-pagination-bullet-active {
    width: 20px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 1;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 56px;
    height: 18px;
    background: v-bind(pinkGradient);
    border-radius: 8px 0px 12px 0px;
    font-weight: 400;
    font-size: 10px;
    color: white !important;
    text-align: center;
  }
}
</style>
